/*
 * df-pdp-gallery-swipe.js
 *
 * Pasar de foto con el dedo en la galeria de la ficha, en movil.
 *
 * --- QUE FALTABA ---
 * 1. Galeria propia (productos sin Teeinblue y fallback `--tee-fallo`): nunca
 *    tuvo swipe. El JS de la seccion solo cambia de foto por miniatura o por
 *    flecha del teclado; en movil el teclado no existe, asi que la unica via
 *    eran las miniaturas. Esta escrito en el propio codigo de la seccion:
 *    «dando por hecho un swipe que nunca llego a programarse».
 * 2. Galeria de Teeinblue: su slider si trae swipe propio (useSwipe sobre
 *    .tee-slider__track), pero se lo comen sus propias capas —el plugin de
 *    pinch-zoom del mockup corta la propagacion cuando cree estar en modo
 *    zoom/drag, y `disable-swipe` se queda pegado tras un doble toque—. Su
 *    codigo no se puede tocar.
 *
 * --- ESTRATEGIA ---
 * Un unico gesto, escuchado en el contenedor `.df-pdp__media` (que sobrevive a
 * los re-montajes de Teeinblue dentro), y dos salidas segun quien mande:
 *
 *   - Con Teeinblue montado: se pulsa SU flecha. No duplicamos su animacion ni
 *     su estado; hacemos lo mismo que haria el cliente tocando el boton.
 *   - Sin Teeinblue: se pulsa la miniatura vecina, que es lo que ya escucha el
 *     JS de la seccion.
 *
 * --- POR QUE NO SE DUPLICA EL PASE ---
 * Si el swipe de Teeinblue SI ha llegado a su handler, al soltar marca la
 * diapositiva destino con `.tee-slide--next` antes de animar. Se comprueba en
 * el frame siguiente al `touchend`: si esa marca esta, el gesto ya es suyo y
 * nosotros no hacemos nada. Solo actuamos cuando su swipe se ha perdido.
 *
 * --- ALCANCE ---
 * Solo <=990px, solo dentro de `.df-pdp__media`, solo escuchas pasivas: no se
 * llama a preventDefault en ningun momento, asi que el scroll vertical y el
 * pinch-zoom de la pagina siguen siendo del navegador. Ver
 * [[zoom-movil-fragil]]: aqui no se toca `touch-action`.
 */
(function () {
  'use strict';

  if (!window.matchMedia || !window.matchMedia('(max-width: 990px)').matches) return;

  /* Umbrales: el gesto tiene que ser claramente horizontal y claramente un
     arrastre, para no robarle el tap a la foto ni el scroll a la pagina. */
  var MIN_X = 40;        // px recorridos en horizontal
  var RATIO = 1.3;       // cuanto mas horizontal que vertical
  var MAX_MS = 800;      // mas lento que esto no es un swipe
  var ESPERA_TEE = 50;   // ms para dejar que Teeinblue marque su destino

  function enThumbs(el) {
    return !!(el && el.closest && el.closest('.tee-thumbnails, .df-pdp__thumbs'));
  }

  /* Teeinblue: pulsar su flecha. Devuelve false si su galeria no manda aqui. */
  function pasarTee(caja, haciaDelante) {
    if (!caja.querySelector('.tee-slider__track')) return false;
    var boton = caja.querySelector(haciaDelante
      ? '.tee-slider__button--next'
      : '.tee-slider__button--prev');
    /* Sin flechas hay una sola diapositiva: no hay nada que pasar, pero la
       galeria es suya igualmente, asi que no cae a la nuestra. */
    if (boton) boton.click();
    return true;
  }

  /* Galeria propia: pulsar la miniatura vecina. Da la vuelta al llegar al
     final, igual que ya hacen las flechas del teclado en la seccion.
     Se abstiene si el escenario ya desliza solo (ver abajo). */
  function pasarPropia(galeria, haciaDelante) {
    /* Desde el 8-sep-2026 el escenario de la seccion es un carrusel con
       scroll nativo (scroll-snap): el dedo lo mueve el navegador. Si ademas
       pulsamos la miniatura vecina, se pasan DOS fotos de un solo gesto.
       Que el escenario tenga desbordamiento horizontal es justo la senal de
       que ya es ese carrusel; si algun dia no lo fuera, esto sigue siendo la
       unica forma de pasar de foto y el gesto se reparte como antes.

       La rama de Teeinblue de arriba no cambia: su galeria sustituye a la
       nuestra y ahi el escenario esta oculto (no desborda), asi que ni
       siquiera llega hasta aqui. */
    var escenario = galeria.querySelector('[data-df-media-stage]');
    if (escenario && escenario.scrollWidth > escenario.clientWidth + 2) return;

    var thumbs = galeria.querySelectorAll('[data-df-thumb]');
    if (thumbs.length < 2) return;
    var activa = 0;
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].classList.contains('is-active')) { activa = i; break; }
    }
    var destino = haciaDelante
      ? (activa + 1) % thumbs.length
      : (activa - 1 + thumbs.length) % thumbs.length;
    thumbs[destino].click();
  }

  function montar(caja) {
    var galeria = caja.closest('.df-pdp__gallery') || caja;
    var x0 = 0, y0 = 0, t0 = 0, valido = false;

    caja.addEventListener('touchstart', function (ev) {
      valido = ev.touches.length === 1 && !enThumbs(ev.target);
      if (!valido) return;
      x0 = ev.touches[0].clientX;
      y0 = ev.touches[0].clientY;
      t0 = Date.now();
    }, { passive: true });

    /* Un segundo dedo a mitad de gesto es un pinch: se abandona. */
    caja.addEventListener('touchmove', function (ev) {
      if (ev.touches.length > 1) valido = false;
    }, { passive: true });

    caja.addEventListener('touchcancel', function () { valido = false; }, { passive: true });

    caja.addEventListener('touchend', function (ev) {
      if (!valido) return;
      valido = false;
      var p = ev.changedTouches[0];
      if (!p) return;
      var dx = p.clientX - x0;
      var dy = p.clientY - y0;
      if (Date.now() - t0 > MAX_MS) return;
      if (Math.abs(dx) < MIN_X) return;
      if (Math.abs(dx) < Math.abs(dy) * RATIO) return;

      var haciaDelante = dx < 0;

      /* Un respiro de cortesia: si el swipe propio de Teeinblue ha llegado a
         su handler, para entonces ya ha marcado el destino con `--next`.
         Va con setTimeout y no con requestAnimationFrame porque este no corre
         cuando la pestana no se esta pintando, y entonces el gesto se perderia
         del todo en vez de repartirse. */
      setTimeout(function () {
        if (caja.querySelector('.tee-slide--next')) return;
        if (pasarTee(caja, haciaDelante)) return;
        pasarPropia(galeria, haciaDelante);
      }, ESPERA_TEE);
    }, { passive: true });
  }

  function arrancar() {
    var cajas = document.querySelectorAll('.df-pdp__media');
    for (var i = 0; i < cajas.length; i++) montar(cajas[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
